import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField as MuiTextField,
  Grid,
} from '@mui/material';
import {
  Description as DocumentsIcon,
} from '@mui/icons-material';
import { useTranslate, useDataProvider, useNotify } from 'react-admin';

interface DocumentsStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  onClose: () => void;
  existingData?: any;
  driver?: any;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

export const DocumentsStep: React.FC<DocumentsStepProps> = ({
  onNext,
  onBack,
  onClose,
  existingData,
  driver,
  isFirstStep,
  isLastStep
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [formData, setFormData] = useState({
    canadianCitizen: null,
    canadaLiveAndWork: null,
    canEnterUsa: null,
    usaEntryStatus: null,
    hasValidPassport: null,
    passportNumber: '',
    passportExpiryDate: '',
    passportCountryOrigin: '',
    fastPass: null,
    usDotNumber: '',
  });

  useEffect(() => {
    if (existingData) {
      setFormData(prev => ({ ...prev, ...existingData }));
    } else if (driver) {
      setFormData(prev => ({
        ...prev,
        canadianCitizen: driver.canadianCitizen ?? null,
        canadaLiveAndWork: driver.canadaLiveAndWork ?? null,
        canEnterUsa: driver.canEnterUsa ?? null,
        usaEntryStatus: driver.usaDenied ?? null,
        hasValidPassport: driver.hasValidPassport ?? null,
        passportNumber: driver.passportNumber || '',
        passportExpiryDate: driver.passportExpiryDate ? driver.passportExpiryDate.split('T')[0] : '',
        passportCountryOrigin: driver.passportCountryOrigin || '',
        fastPass: driver.fastPass ?? null,
        usDotNumber: driver.usDotNumber || '',
      }));
    }
  }, [existingData, driver]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    // Map usaEntryStatus back to usaDenied for the API
    const { usaEntryStatus, ...otherData } = formData;
    const saveData = {
      ...otherData,
      usaDenied: usaEntryStatus,
    };
    onNext(saveData);
  };

  const renderYesNoField = (
    label: string,
    field: string,
    descriptionField?: string
  ) => (
    <Grid item xs={12}>
      <Paper elevation={0} sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        bgcolor: 'white',
        minWidth: '600px',
        width: '100%',
      }}>
        <Typography variant="h6" sx={{ mb: 2, wordWrap: 'break-word' }}>
          {label}
        </Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select an option</InputLabel>
          <Select
            value={formData[field] === null ? '' : formData[field].toString()}
            onChange={(e) => handleInputChange(field, e.target.value === '' ? null : e.target.value === 'true')}
            label="Select an option"
          >
            <MenuItem value="">Not specified</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
        {formData[field] === true && descriptionField && (
          <MuiTextField
            fullWidth
            multiline
            rows={3}
            label="Please provide details"
            value={formData[descriptionField]}
            onChange={(e) => handleInputChange(descriptionField, e.target.value)}
            placeholder="Provide additional details..."
          />
        )}
      </Paper>
    </Grid>
  );

  return (
    <Box sx={{ maxWidth: '90vw', mx: 'auto', p: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 600, mb: 1, color: 'grey.800' }}>
          <DocumentsIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Documents & Citizenship
        </Typography>
        <Typography variant="body1" sx={{ color: 'grey.600', fontSize: '1.1rem' }}>
          Please provide your citizenship and travel document information
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {renderYesNoField(
          'Are you a Canadian Citizen?',
          'canadianCitizen'
        )}

        {renderYesNoField(
          'Can you live and work in Canada?',
          'canadaLiveAndWork'
        )}

        {renderYesNoField(
          'Can you enter the USA?',
          'canEnterUsa'
        )}

        {renderYesNoField(
          'Have you ever been denied entry to the USA?',
          'usaEntryStatus'
        )}

        <Grid item xs={12}>
          <Paper elevation={0} sx={{
            p: 3,
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            bgcolor: 'white',
            minWidth: '600px',
            width: '100%',
          }}>
            <Typography variant="h6" sx={{ mb: 2, wordWrap: 'break-word' }}>
              Do you have a valid passport?
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select an option</InputLabel>
              <Select
                value={formData.hasValidPassport === null ? '' : formData.hasValidPassport.toString()}
                onChange={(e) => handleInputChange('hasValidPassport', e.target.value === '' ? null : e.target.value === 'true')}
                label="Select an option"
              >
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
            {formData.hasValidPassport === true && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Passport Details
                </Typography>
                <Stack spacing={2}>
                  <MuiTextField
                    fullWidth
                    label="Passport Number"
                    value={formData.passportNumber}
                    onChange={(e) => handleInputChange('passportNumber', e.target.value)}
                    placeholder="Enter passport number"
                  />
                  <MuiTextField
                    fullWidth
                    label="Expiry Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={formData.passportExpiryDate}
                    onChange={(e) => handleInputChange('passportExpiryDate', e.target.value)}
                  />
                  <MuiTextField
                    fullWidth
                    label="Issuing Country"
                    value={formData.passportCountryOrigin}
                    onChange={(e) => handleInputChange('passportCountryOrigin', e.target.value)}
                    placeholder="Enter issuing country"
                  />
                </Stack>
              </Box>
            )}
          </Paper>
        </Grid>

        {renderYesNoField(
          'Do you have a FAST Pass?',
          'fastPass'
        )}

        <Grid item xs={12}>
          <Paper elevation={0} sx={{
            p: 3,
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            bgcolor: 'white',
            minWidth: '600px',
            width: '100%',
          }}>
            <Typography variant="h6" sx={{ mb: 2, wordWrap: 'break-word' }}>
              US DOT Number (if applicable)
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <MuiTextField
                fullWidth
                label="Enter US DOT Number"
                value={formData.usDotNumber}
                onChange={(e) => handleInputChange('usDotNumber', e.target.value)}
                placeholder="Enter US DOT Number (if applicable)"
              />
            </FormControl>
          </Paper>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        mt: 4,
        pt: 4,
        borderTop: '1px solid',
        borderColor: 'grey.200'
      }}>
        {!isFirstStep && (
          <Button
            variant="outlined"
            size="large"
            onClick={onBack}
            sx={{ px: 4, py: 1.5 }}
          >
            Back
          </Button>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="contained"
          size="large"
          onClick={handleNext}
          sx={{ px: 4, py: 1.5 }}
        >
          {isLastStep ? 'Complete' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};